import { useState } from "react";

interface PaginationProps {
  current: number;
  total: number;
  onChange: (page: number) => void;
}

function buildPages(current: number, total: number): Array<number | "ellipsis"> {
  const pages: Array<number | "ellipsis"> = [];
  if (total <= 7) {
    for (let i = 1; i <= total; i += 1) pages.push(i);
    return pages;
  }
  pages.push(1);
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) pages.push("ellipsis");
  for (let i = start; i <= end; i += 1) pages.push(i);
  if (end < total - 1) pages.push("ellipsis");
  pages.push(total);
  return pages;
}

export default function Pagination({ current, total, onChange }: PaginationProps) {
  const [jump, setJump] = useState("");

  if (total <= 1) return null;

  const handleJump = () => {
    const value = Number(jump);
    if (!Number.isNaN(value) && value >= 1 && value <= total) {
      onChange(value);
      setJump("");
    }
  };

  return (
    <div className="flex flex-wrap items-center justify-center gap-2">
      <button
        type="button"
        onClick={() => onChange(Math.max(1, current - 1))}
        disabled={current === 1}
        className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
        aria-label="上一页"
      >
        <i className="ri-arrow-left-s-line text-lg"></i>
      </button>

      {buildPages(current, total).map((page, index) =>
        page === "ellipsis" ? (
          <span key={`e-${index}`} className="px-1 text-foreground-400">
            …
          </span>
        ) : (
          <button
            key={page}
            type="button"
            onClick={() => onChange(page)}
            className={`h-9 min-w-9 rounded-md border px-3 text-sm transition-colors cursor-pointer ${
              page === current
                ? "border-primary-500 bg-primary-500 text-background-50"
                : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
            }`}
          >
            {page}
          </button>
        )
      )}

      <button
        type="button"
        onClick={() => onChange(Math.min(total, current + 1))}
        disabled={current === total}
        className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
        aria-label="下一页"
      >
        <i className="ri-arrow-right-s-line text-lg"></i>
      </button>

      <div className="ml-2 flex items-center gap-2 text-sm text-foreground-500">
        <span className="whitespace-nowrap">跳至</span>
        <input
          type="number"
          min={1}
          max={total}
          value={jump}
          onChange={(e) => setJump(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleJump();
          }}
          className="h-9 w-14 rounded-md border border-background-300 bg-background-50 px-2 text-center text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
          aria-label="跳转页码"
        />
        <button
          type="button"
          onClick={handleJump}
          className="h-9 rounded-md bg-secondary-500 px-3 text-sm text-background-50 transition-colors hover:bg-secondary-600 cursor-pointer whitespace-nowrap"
        >
          跳转
        </button>
      </div>
    </div>
  );
}